import { ModelRegistry } from './model-registry';
import { Logger } from './logger';

export interface ModelClass<T> {
    ROOT_URL? : string;
    API? : Function;

    new(data?) : T;

    where(criteria : any, meta? : any): Promise<T[]>;
    find(id : any): Promise<T>;
    findBy(id : any);
    all(meta? : any): Promise<T[]>;
    expand<T>(this: { new(data?) : T }, items : any[]) : T[];
}

/**
 * Base class for API-backed models.
 * Subclass and declare a static field "ROOT_URL" to specify the REST collection being used.
 * Defaults to ApiService.tyt for the root endpoint. Specify "API" to choose the field on ApiService
 * that this model goes with.
 */
export class BaseModel {
    constructor(data?) {
        this.adapt(data);
    }

    static newFromArray<T>(this : { new(...args) : T }, data: any[]): T[] {
        return data.map(x => new this(x));
    }

    static ROOT_URL = null;
    static API = null;

    /**
     * Get the class of this model.
     */
    get class() : ModelClass<this> {
        return <any>this['constructor'];
    }

    /**
     * ID of this item.
     */
    id? : any;

    /**
     * Get the data of this model instance as a JSON string.
     */
    asJSON() {
        return JSON.stringify(this, (key, value) => {
            if (key && key[0] == '_')
                return undefined;
            
            return value;
        });
    }

    /**
     * Get the data of this model instance as a plain old object.
     */
    getData() {
        return JSON.parse(this.asJSON());
    }

    private identifyModelName(key : string, value : any) {

        // By default, our type is the value type, whatever that is.
        // We'll upgrade it later, so `typeof value` is mostly used for 
        // special types 'null' and 'undefined'.

        let isArray = false;
        let modelType = null;
        let type : Function = null;
        let typeName : string = null;

        // If the object has a constructor, use that as the type of object 
        // instead of a string name.
        
        if (value !== null && value !== undefined && value.constructor) {
            type = value.constructor;
        }

        // Look up the Typescript metadata for this field on the model class.
        // If we find a usable type, use it.

        let designType : Function | string = Reflect.getMetadata("design:type", this, key);
        if (typeof designType === 'function')
            type = designType;
        else 
            typeName = designType;
    
        // If the class we get back is Array, use the additional 
        // arrayType metadata to get the type of the array. 

        if (type === Array) {
            isArray = true;
            type = null;

            let arrayType = Reflect.getMetadata("design:arrayType", this, key);
            if (typeof arrayType === 'function')
                type = arrayType;
            else 
                typeName = arrayType;
        }

        // If we ended up with a constructor (class), go ahead and 
        // look up the corresponding model name for it.

        if (typeof type === 'function')
            typeName = ModelRegistry.getNameForClass(type);

        // Remove the 'Api' prefix from the model name.

        if (typeName)
            typeName = typeName.replace(/^Api/, '');

        return typeName;
    }

    private _sanitized : Record<string,any> = {};

    /**
     * Update this model instance locally with the data in the given object.
     * @param data The data to apply
     */
    adapt(data) {

        if (typeof data != 'object')
            throw new Error(`You cannot adapt() a value of type '${typeof data}'. Value must be an object.`);

        this._sanitized = {};
        for (let key in data) {

            // Skip the sanitization information on the object, if present
            // This is used with the DomSanitizer model features. CAUTION:
            // Traversing this key using adapt() can cause infinite traversal 
            // without skipping it here.

            if (key == '_sanitized')   
                continue;

            let value = data[key];
            let modelType = this.identifyModelName(key, value);

            // If we have the name of a model type, go ahead and inflate the value 
            // to be an instance of it, using ModelRegistry.inflate().

            if (modelType) {
                if (modelType == 'Array') {
                    Logger.failure(`While adapting data: Could not identify array type`);
                    debugger;
                }

                value = ModelRegistry.inflate(modelType, value);
            }

            // Assign the resulting value into our own object.
            // At this point, `value` is either the raw value from the JSON 
            // entity data, or is an instance of a model class.

            try {
                this[key] = value;
            } catch (e) {
                Logger.error(`Failed to set key ${key}: ${e.message}`, e);
            }
        }
    }

    static getClass<T>(cls: { new(data?) : T }) : ModelClass<T> {
        return <ModelClass<T>>cls;
    }

    static prepareCriteria(criteria : any) {
        if (!criteria)
            return {};

        // clone the criteria

        criteria = JSON.parse(JSON.stringify(criteria));
        
        // locate any references to model objects and replace them with their corresponding IDs if possible
        // also delete any keys with undefined as a value

        for (let key in criteria) {
            let value = criteria[key];

            if (value === undefined)
                delete criteria[key];
            else if (value instanceof BaseModel && value.id) {
                delete criteria[key];
                if (key.endsWith('_id'))
                    criteria[key] = value.id;
                else
                    criteria[`${key}_id`] = value.id;
            }
        }

        return criteria;
    }

    /**
     * Quickly create model instances for all data items in an array.
     * @param this The model class 
     * @param items An array of data items to use for instantiating the objects.
     */
    static expand<T>(this: { new(data?) : T }, items : any[]) : T[] {
        return items.map(x => new this(x));
    }

    /**
     * Fetch all instances of this model class.
     * @param this 
     */
    static all<T>(this: { new(data?) : T }, count? : number, offset? : number): Promise<T[]> {
        let api = (<any>this).getApi(this)

        if (!api.all)
            throw new Error(`${api.constructor.name} does not provide all()`);

        return api
            .all(JSON.stringify(BaseModel.prepareCriteria({ count: count, offset: offset })))
            .map(x => x.json ? x.json() : x)
            .toPromise()
            .catch(response => { throw response.json ? response.json() : response; })
        ; 
    }

    /**
     * Find a specific model instance by arbitrary criteria
     * @param this The model class 
     * @param id The ID to search for 
     */
    static async findBy<T>(this: { new(data?) : T }, criteria : any): Promise<T> {
        criteria = BaseModel.prepareCriteria(criteria);
        criteria.limit = 1;
        let api = (<any>this).getApi(this);

        if (!api.all)
            throw new Error(`${api.constructor.name} does not provide all()`);

        let results = await api
            .all(JSON.stringify(criteria))
            .map(x => x.json ? x.json() : x)
            .toPromise()
            .catch(response => { throw response.json ? response.json() : response; })
        ;

        return results[0];
    }

    /**
     * Fetch all matching model instances based on the given criteria
     * @param this The model class
     * @param criteria The criteria to search for
     */
    static where<T>(this: { new(data?) : T }, criteria : any): Promise<T[]> {
        criteria = BaseModel.prepareCriteria(criteria);
        let api = (<any>this).getApi(this);

        if (!api.all)
        throw new Error(`${api.constructor.name} does not provide all()`);

        return api
            .all(JSON.stringify(criteria))
            .map(x => x.json ? x.json() : x)
            .toPromise()
            .catch(response => { throw response.json ? response.json() : response; })
        ;
    } 
}

